import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RewardsTab = ({ rewards, onRefresh }) => {
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardData, setRewardData] = useState({
    name: '',
    description: '',
    cost: 0,
    type: 'prize',
    icon: '🏆',
    image_url: '',
    redemption_link: '',
  });

  const createReward = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/admin/rewards`, rewardData);
      toast.success('Recompensa criada com sucesso!');
      setShowRewardForm(false);
      setRewardData({
        name: '',
        description: '',
        cost: 0,
        type: 'prize',
        icon: '🏆',
        image_url: '',
        redemption_link: '',
      });
      onRefresh();
    } catch (error) {
      toast.error('Erro ao criar recompensa');
    }
  };

  const updateReward = async (rewardId, data) => {
    try {
      await axios.patch(`${API}/api/admin/rewards/${rewardId}`, data);
      toast.success('Recompensa atualizada!');
      setEditingReward(null);
      onRefresh();
    } catch (error) {
      toast.error('Erro ao atualizar recompensa');
    }
  };

  const deleteReward = async (rewardId) => {
    if (!window.confirm('Excluir esta recompensa?')) return;
    try {
      await axios.delete(`${API}/api/admin/rewards/${rewardId}`);
      toast.success('Recompensa excluída');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao excluir recompensa');
    }
  };

  return (
    <div className="space-y-4">
      <Button
        onClick={() => setShowRewardForm(!showRewardForm)}
        className="bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80"
        data-testid="new-reward-button"
      >
        <Plus className="w-5 h-5 mr-2" />
        Nova Recompensa
      </Button>

      {showRewardForm && (
        <motion.div
          className="bg-zinc-900/50 border border-neon-lime/30 backdrop-blur-md rounded-2xl p-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="reward-form"
        >
          <h2 className="font-primary font-bold text-2xl text-white uppercase mb-4">
            Criar Recompensa
          </h2>
          <form onSubmit={createReward} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Nome</label>
              <Input
                type="text"
                value={rewardData.name}
                onChange={(e) => setRewardData({ ...rewardData, name: e.target.value })}
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
                data-testid="reward-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Descrição</label>
              <Input
                type="text"
                value={rewardData.description}
                onChange={(e) => setRewardData({ ...rewardData, description: e.target.value })}
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
                data-testid="reward-description-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2 flex items-center gap-2">
                <img src="/fercoin.png" alt="FerCoin" className="w-4 h-4" />
                Custo (FerCoin)
              </label>
              <Input
                type="number"
                value={rewardData.cost}
                onChange={(e) => setRewardData({ ...rewardData, cost: parseInt(e.target.value) })}
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
                data-testid="reward-cost-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Ícone (emoji)</label>
              <Input
                type="text"
                value={rewardData.icon}
                onChange={(e) => setRewardData({ ...rewardData, icon: e.target.value })}
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                data-testid="reward-icon-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Imagem de Thumbnail (URL)</label>
              <Input
                type="url"
                value={rewardData.image_url}
                onChange={(e) => setRewardData({ ...rewardData, image_url: e.target.value })}
                placeholder="https://exemplo.com/imagem.jpg"
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                data-testid="reward-image-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Cole a URL de uma imagem para exibir na recompensa</p>
              {rewardData.image_url && (
                <div className="mt-2">
                  <img 
                    src={rewardData.image_url} 
                    alt="Preview" 
                    className="w-20 h-20 object-cover rounded-lg border border-zinc-700"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Link de Resgate (opcional)</label>
              <Input
                type="url"
                value={rewardData.redemption_link}
                onChange={(e) => setRewardData({ ...rewardData, redemption_link: e.target.value })}
                placeholder="https://exemplo.com/resgate"
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                data-testid="reward-link-input"
              />
              <p className="text-xs text-zinc-500 mt-1">O aluno será direcionado para este link ao resgatar a recompensa</p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80" data-testid="submit-reward-button">
                Criar Recompensa
              </Button>
              <Button type="button" onClick={() => setShowRewardForm(false)} className="bg-zinc-800 text-white hover:bg-zinc-700">
                Cancelar
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Lista de Recompensas */}
      <div className="space-y-3 mt-6">
        <h3 className="text-lg font-bold text-white">Recompensas Cadastradas ({rewards.length})</h3>
        {rewards.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">Nenhuma recompensa cadastrada</p>
        ) : (
          rewards.map((reward) => (
            <motion.div
              key={reward.id}
              className="bg-zinc-900/50 border border-white/5 rounded-xl p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {editingReward === reward.id ? (
                <div className="space-y-3">
                  <Input
                    defaultValue={reward.name}
                    id={`edit-reward-name-${reward.id}`}
                    placeholder="Nome"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    defaultValue={reward.description}
                    id={`edit-reward-desc-${reward.id}`}
                    placeholder="Descrição"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    type="number"
                    defaultValue={reward.cost}
                    id={`edit-reward-cost-${reward.id}`}
                    placeholder="Custo"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    defaultValue={reward.image_url || ''}
                    id={`edit-reward-image-${reward.id}`}
                    placeholder="URL da imagem (https://...)"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    defaultValue={reward.redemption_link || ''}
                    id={`edit-reward-link-${reward.id}`}
                    placeholder="Link de resgate (https://...)"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => updateReward(reward.id, {
                        name: document.getElementById(`edit-reward-name-${reward.id}`).value,
                        description: document.getElementById(`edit-reward-desc-${reward.id}`).value,
                        cost: parseInt(document.getElementById(`edit-reward-cost-${reward.id}`).value),
                        image_url: document.getElementById(`edit-reward-image-${reward.id}`).value || null,
                        redemption_link: document.getElementById(`edit-reward-link-${reward.id}`).value || null,
                      })}
                      className="bg-neon-lime text-black"
                    >
                      Salvar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingReward(null)}
                      className="border-zinc-700 text-zinc-400"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {reward.image_url ? (
                      <img 
                        src={reward.image_url} 
                        alt={reward.name}
                        className="w-16 h-16 object-cover rounded-lg border border-zinc-700"
                      />
                    ) : (
                      <span className="text-3xl">{reward.icon}</span>
                    )}
                    <div>
                      <h4 className="font-bold text-white">{reward.name}</h4>
                      <p className="text-sm text-zinc-400">{reward.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-yellow-400 font-bold text-sm flex items-center gap-1">
                          <img src="/fercoin.png" alt="FC" className="w-4 h-4" />
                          {reward.cost} FC
                        </span>
                        {reward.redemption_link && (
                          <a 
                            href={reward.redemption_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Link de resgate
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingReward(reward.id)}
                      className="border-zinc-700 text-zinc-400 hover:text-white"
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteReward(reward.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default RewardsTab;
